import {
  humidityRatioFromRelativeHumidity,
  humidityRatioFromThermodynamicWetBulb,
} from '../../server/tmy/psychrometrics';
import {
  TMY_DATASET_SCHEMA_VERSION,
  type TmyDataset,
  type TmyHour,
} from '../../types/tmy';

/**
 * Hourly, climate-specific screening model for adiabatic pre-cooling water.
 *
 * This is deliberately separate from the benchmark water-use model. It
 * estimates water added to the inlet-air stream; it does not certify that a
 * selected dry cooler can reject the design heat load at the resulting air
 * temperature.
 */
export const ADIABATIC_WATER_SCREENING_MODEL_VERSION =
  'adiabatic-water-screening-v2' as const;

const REQUIRED_TMY_HOURS = 8_760;
const HOURS_PER_DAY = 24;
const DAYS_PER_NON_LEAP_YEAR = REQUIRED_TMY_HOURS / HOURS_PER_DAY;
const SECONDS_PER_HOUR = 3_600;
const KELVIN_OFFSET = 273.15;
const KILOPASCALS_PER_MEGAPASCAL = 1_000;
const DRY_AIR_GAS_CONSTANT_KPA_M3_PER_KG_K = 0.287_042;
const HUMIDITY_SPECIFIC_VOLUME_FACTOR = 1.607_858;
const WATER_DENSITY_KG_PER_M3 = 1_000;
const US_GALLONS_PER_CUBIC_METER = 264.172_052_358_148_4;
const HUMIDITY_RATIO_CONSISTENCY_TOLERANCE = 1e-6;
const NEGATIVE_HUMIDITY_GAIN_TOLERANCE = 1e-9;

export interface AdiabaticScreeningRange {
  readonly low: number;
  readonly high: number;
}

export interface AdiabaticWaterScreeningInput {
  readonly tmy: TmyDataset;
  /** Installed/design facility heat-rejection capacity before utilization. */
  readonly facilityHeatMw: AdiabaticScreeningRange;
  /** Annual operating-load fraction, expressed from 0 to 1. */
  readonly utilization: AdiabaticScreeningRange;
  readonly airflowM3sPerMw?: AdiabaticScreeningRange;
  readonly saturationEffectiveness?: AdiabaticScreeningRange;
  readonly activationThresholdC?: AdiabaticScreeningRange;
  readonly cyclesOfConcentration?: AdiabaticScreeningRange;
}

export interface AdiabaticWaterScreeningSourceRef {
  readonly id: string;
  readonly publisher: string;
  readonly title: string;
  readonly url: string;
  readonly locator: string;
  readonly supports: readonly string[];
}

export const ADIABATIC_WATER_SCREENING_SOURCE_REFS:
readonly AdiabaticWaterScreeningSourceRef[] = [
  {
    id: 'ashrae-fundamentals-2021-ch1-psychrometrics',
    publisher: 'ASHRAE',
    title: '2021 ASHRAE Handbook—Fundamentals, Chapter 1: Psychrometrics',
    url: 'https://handbook.ashrae.org/Handbooks/F21/SI/F21_Ch01/F21_Ch01_si.aspx',
    locator: 'Equations 21, 27/28, and 33/35',
    supports: [
      'pressure-aware humidity ratio',
      'moist-air specific volume on a dry-air basis',
      'thermodynamic wet-bulb relation',
    ],
  },
  {
    id: 'evapco-adiabatic-pad-saturation-efficiency-2019',
    publisher: 'EVAPCO',
    title: 'Adiabatic Fluid Coolers & Refrigerant Condensers: Impact of Pad System Design on Saturation Efficiency and Unit Operation',
    url: 'https://www.evapco.com/media/341302',
    locator: 'Executive summary and Equation 1; tested 400-700 fpm endpoint results',
    supports: [
      'outlet dry-bulb effectiveness equation',
      '72-78% tested saturation-effectiveness screening range',
    ],
  },
  {
    id: 'spx-olympusv-adiabatic-engineering-data-2025',
    publisher: 'SPX Cooling Technologies',
    title: 'Marley OlympusV Adiabatic Fluid Cooler Engineering Data',
    url: 'https://spxcooling.com/wp-content/uploads/TECH-OLYAD-25A.pdf',
    locator: 'Engineering-data tables: dry heat rejection and airflow rate',
    supports: [
      '105-115 m3/s per MW generic airflow screening envelope',
    ],
  },
  {
    id: 'spx-managing-water-adiabatic-systems-2025',
    publisher: 'SPX Cooling Technologies',
    title: 'Managing Water of Adiabatic Systems',
    url: 'https://spxcooling.com/wp-content/uploads/TP25-25-Managing-Water-of-Adiabatic-Systems-CTI-Paper-Final-Draft-1172025.pdf',
    locator: 'Sections 2, 4, and 6; Table 3',
    supports: [
      'humidity-ratio evaporation mass balance',
      'recirculating makeup and cycles-of-concentration basis',
      '80 F dry-bulb activation screening case',
    ],
  },
];

export const DEFAULT_ADIABATIC_WATER_SCREENING_ASSUMPTIONS = {
  airflowM3sPerMw: { low: 105, high: 115 },
  saturationEffectiveness: { low: 0.72, high: 0.78 },
  activationThresholdC: { low: 26.67, high: 29.44 },
  cyclesOfConcentration: { low: 3, high: 5 },
} as const;

export interface AdiabaticWaterEndpointTrace {
  readonly endpoint: 'low' | 'high';
  readonly facilityHeatMw: number;
  readonly utilization: number;
  readonly operatingFacilityHeatMw: number;
  readonly airflowM3sPerMw: number;
  readonly totalAirflowM3s: number;
  readonly saturationEffectiveness: number;
  readonly activationThresholdC: number;
  readonly cyclesOfConcentration: number;
  readonly activeHours: number;
  readonly annualEvaporationGallons: number;
  readonly annualMakeupGallons: number;
  readonly averageDailyEvaporationGallons: number;
  readonly averageDailyMakeupGallons: number;
  /** Largest total from any contiguous 24-hour TMY calendar-day block. */
  readonly peakDailyEvaporationGallons: number;
  /** Recirculating makeup for the same peak-evaporation TMY day. */
  readonly peakDailyMakeupGallons: number;
  readonly peakDay: {
    /** Zero-based 24-hour day block within the 8,760-hour TMY. */
    readonly tmyDayIndex: number;
    readonly startHour: number;
    readonly month: number;
    readonly day: number;
    readonly activeHours: number;
  };
  readonly peakActiveHourEvaporationGallonsPerHour: number;
  readonly peakActiveHourMakeupGallonsPerHour: number;
}

export interface AdiabaticWaterScreeningCalculatedResult {
  readonly status: 'calculated';
  readonly modelVersion: typeof ADIABATIC_WATER_SCREENING_MODEL_VERSION;
  readonly estimateBasis: 'tmy-hourly-psychrometric-screening';
  readonly unavailableReason: null;
  readonly validationErrors: readonly [];
  readonly annualEvaporationGallons: AdiabaticScreeningRange;
  readonly annualMakeupGallons: AdiabaticScreeningRange;
  readonly averageDailyEvaporationGallons: AdiabaticScreeningRange;
  readonly averageDailyMakeupGallons: AdiabaticScreeningRange;
  readonly peakDailyEvaporationGallons: AdiabaticScreeningRange;
  readonly peakDailyMakeupGallons: AdiabaticScreeningRange;
  readonly activeHours: AdiabaticScreeningRange;
  readonly peakActiveHourEvaporationGallonsPerHour: AdiabaticScreeningRange;
  readonly peakActiveHourMakeupGallonsPerHour: AdiabaticScreeningRange;
  readonly assumptions: {
    readonly facilityHeatMw: AdiabaticScreeningRange;
    readonly utilization: AdiabaticScreeningRange;
    readonly operatingFacilityHeatMw: AdiabaticScreeningRange;
    readonly airflowM3sPerMw: AdiabaticScreeningRange;
    readonly saturationEffectiveness: AdiabaticScreeningRange;
    readonly activationThresholdC: AdiabaticScreeningRange;
    readonly cyclesOfConcentration: AdiabaticScreeningRange;
    readonly waterDensityKgPerM3: number;
  };
  readonly trace: {
    readonly validTmyHours: typeof REQUIRED_TMY_HOURS;
    readonly hourDurationSeconds: typeof SECONDS_PER_HOUR;
    readonly activationRule: 'dryBulbC >= activationThresholdC';
    readonly endpointMethod: string;
    readonly equations: readonly string[];
    readonly endpoints: readonly [
      AdiabaticWaterEndpointTrace,
      AdiabaticWaterEndpointTrace,
    ];
  };
  readonly provenance: {
    readonly tmy: {
      readonly schemaVersion: typeof TMY_DATASET_SCHEMA_VERSION;
      readonly source: TmyDataset['source'];
      readonly sourceVersion?: string;
      readonly datasetName?: string;
      readonly datasetYear: number | null;
      readonly latitudeRounded: number;
      readonly longitudeRounded: number;
      readonly gridLatitude?: number;
      readonly gridLongitude?: number;
      readonly locationId?: string;
      readonly elevationM: number;
      readonly timeZoneOffsetHours: number;
      readonly fetchedAt: string;
      readonly hourCount: typeof REQUIRED_TMY_HOURS;
    };
    readonly sources: readonly AdiabaticWaterScreeningSourceRef[];
  };
  readonly limitations: readonly string[];
}

export interface AdiabaticWaterScreeningUnavailableResult {
  readonly status: 'not-calculated';
  readonly modelVersion: typeof ADIABATIC_WATER_SCREENING_MODEL_VERSION;
  readonly estimateBasis: 'tmy-hourly-psychrometric-screening';
  readonly unavailableReason: 'invalid-input' | 'invalid-tmy';
  readonly validationErrors: readonly string[];
  readonly limitations: readonly string[];
}

export type AdiabaticWaterScreeningResult =
  | AdiabaticWaterScreeningCalculatedResult
  | AdiabaticWaterScreeningUnavailableResult;

interface ResolvedAssumptions {
  facilityHeatMw: AdiabaticScreeningRange;
  utilization: AdiabaticScreeningRange;
  airflowM3sPerMw: AdiabaticScreeningRange;
  saturationEffectiveness: AdiabaticScreeningRange;
  activationThresholdC: AdiabaticScreeningRange;
  cyclesOfConcentration: AdiabaticScreeningRange;
}

interface EndpointConfiguration {
  endpoint: AdiabaticWaterEndpointTrace['endpoint'];
  facilityHeatMw: number;
  utilization: number;
  airflowM3sPerMw: number;
  saturationEffectiveness: number;
  activationThresholdC: number;
  cyclesOfConcentration: number;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function validateRange(
  range: AdiabaticScreeningRange | undefined,
  field: string,
  options: {
    min: number;
    minExclusive?: boolean;
    max?: number;
    maxExclusive?: boolean;
  },
): string[] {
  if (!range || !isFiniteNumber(range.low) || !isFiniteNumber(range.high)) {
    return [`${field} must contain finite low and high values.`];
  }
  if (range.high < range.low) {
    return [`${field}.high must be greater than or equal to ${field}.low.`];
  }
  const lowInvalid = options.minExclusive
    ? range.low <= options.min
    : range.low < options.min;
  if (lowInvalid) {
    return [
      `${field}.low must be ${options.minExclusive ? 'greater than' : 'at least'} ${String(options.min)}.`,
    ];
  }
  if (options.max !== undefined) {
    const highInvalid = options.maxExclusive
      ? range.high >= options.max
      : range.high > options.max;
    if (highInvalid) {
      return [
        `${field}.high must be ${options.maxExclusive ? 'less than' : 'at most'} ${String(options.max)}.`,
      ];
    }
  }
  return [];
}

function resolveAssumptions(input: AdiabaticWaterScreeningInput): ResolvedAssumptions {
  return {
    facilityHeatMw: input.facilityHeatMw,
    utilization: input.utilization,
    airflowM3sPerMw: input.airflowM3sPerMw
      ?? DEFAULT_ADIABATIC_WATER_SCREENING_ASSUMPTIONS.airflowM3sPerMw,
    saturationEffectiveness: input.saturationEffectiveness
      ?? DEFAULT_ADIABATIC_WATER_SCREENING_ASSUMPTIONS.saturationEffectiveness,
    activationThresholdC: input.activationThresholdC
      ?? DEFAULT_ADIABATIC_WATER_SCREENING_ASSUMPTIONS.activationThresholdC,
    cyclesOfConcentration: input.cyclesOfConcentration
      ?? DEFAULT_ADIABATIC_WATER_SCREENING_ASSUMPTIONS.cyclesOfConcentration,
  };
}

function validateInput(assumptions: ResolvedAssumptions): string[] {
  const errors = [
    ...validateRange(assumptions.facilityHeatMw, 'facilityHeatMw', {
      min: 0,
      minExclusive: true,
    }),
    ...validateRange(assumptions.utilization, 'utilization', {
      min: 0,
      minExclusive: true,
      max: 1,
    }),
    ...validateRange(assumptions.airflowM3sPerMw, 'airflowM3sPerMw', {
      min: 0,
      minExclusive: true,
    }),
    ...validateRange(assumptions.saturationEffectiveness, 'saturationEffectiveness', {
      min: 0,
      minExclusive: true,
      max: 1,
      maxExclusive: true,
    }),
    ...validateRange(assumptions.activationThresholdC, 'activationThresholdC', {
      min: -100,
      max: 80,
    }),
    ...validateRange(assumptions.cyclesOfConcentration, 'cyclesOfConcentration', {
      min: 1,
      minExclusive: true,
    }),
  ];

  if (errors.length === 0) {
    const maximumAirflow = assumptions.facilityHeatMw.high
      * assumptions.utilization.high
      * assumptions.airflowM3sPerMw.high;
    if (!Number.isFinite(maximumAirflow)) {
      errors.push('The resolved maximum total airflow must be finite.');
    }
  }
  return errors;
}

function validateHour(hour: TmyHour, expectedIndex: number): string | null {
  if (!hour || typeof hour !== 'object') {
    return 'record must be a non-null object.';
  }
  if (hour.hour !== expectedIndex) {
    return `hour must equal its zero-based position ${String(expectedIndex)}.`;
  }
  if (!Number.isInteger(hour.month) || hour.month < 1 || hour.month > 12) {
    return 'month must be an integer from 1 to 12.';
  }
  if (!Number.isInteger(hour.day) || hour.day < 1 || hour.day > 31) {
    return 'day must be an integer from 1 to 31.';
  }
  if (!Number.isInteger(hour.hourOfDay) || hour.hourOfDay < 0 || hour.hourOfDay > 23) {
    return 'hourOfDay must be an integer from 0 to 23.';
  }
  const finiteFields: ReadonlyArray<readonly [string, number]> = [
    ['dryBulbC', hour.dryBulbC],
    ['wetBulbC', hour.wetBulbC],
    ['relativeHumidity', hour.relativeHumidity],
    ['pressureMPa', hour.pressureMPa],
    ['windSpeedMs', hour.windSpeedMs],
    ['ghi_Wm2', hour.ghi_Wm2],
    ['dni_Wm2', hour.dni_Wm2],
    ['dhi_Wm2', hour.dhi_Wm2],
  ];
  for (const [field, value] of finiteFields) {
    if (!Number.isFinite(value)) return `${field} must be finite.`;
  }
  if (hour.dryBulbC < -100 || hour.dryBulbC > 80) {
    return 'dryBulbC must be between -100 and 80 C.';
  }
  if (hour.wetBulbC < -100 || hour.wetBulbC > hour.dryBulbC) {
    return 'wetBulbC must be between -100 C and dryBulbC.';
  }
  if (hour.relativeHumidity < 0 || hour.relativeHumidity > 1) {
    return 'relativeHumidity must be between 0 and 1.';
  }
  if (hour.pressureMPa < 0.02 || hour.pressureMPa > 0.2) {
    return 'pressureMPa must be plausible absolute atmospheric pressure (0.02-0.2 MPa).';
  }

  try {
    const ratioFromRh = humidityRatioFromRelativeHumidity(hour);
    const ratioFromWetBulb = humidityRatioFromThermodynamicWetBulb(
      hour.dryBulbC,
      hour.wetBulbC,
      hour.pressureMPa,
    );
    if (!Number.isFinite(ratioFromRh) || ratioFromRh < 0) {
      return 'inlet humidity ratio must be finite and non-negative.';
    }
    if (!Number.isFinite(ratioFromWetBulb) || ratioFromWetBulb < 0) {
      return 'wet-bulb humidity ratio must be finite and non-negative.';
    }
    if (Math.abs(ratioFromRh - ratioFromWetBulb)
      > HUMIDITY_RATIO_CONSISTENCY_TOLERANCE) {
      return 'dry bulb, wet bulb, relative humidity, and pressure are psychrometrically inconsistent.';
    }
  } catch (error) {
    return error instanceof Error ? error.message : 'psychrometric validation failed.';
  }
  return null;
}

export function tmyDatasetValidationErrors(tmy: TmyDataset): string[] {
  if (!tmy || typeof tmy !== 'object') return ['TMY dataset is required.'];
  if (tmy.schemaVersion !== TMY_DATASET_SCHEMA_VERSION) {
    return [
      `TMY schemaVersion must be ${String(TMY_DATASET_SCHEMA_VERSION)}.`,
    ];
  }
  if (!Array.isArray(tmy.hours) || tmy.hours.length !== REQUIRED_TMY_HOURS) {
    return [`TMY must contain exactly ${String(REQUIRED_TMY_HOURS)} hourly records.`];
  }
  if (!isFiniteNumber(tmy.latitudeRounded)
    || tmy.latitudeRounded < -90
    || tmy.latitudeRounded > 90) {
    return ['TMY latitudeRounded must be between -90 and 90.'];
  }
  if (!isFiniteNumber(tmy.longitudeRounded)
    || tmy.longitudeRounded < -180
    || tmy.longitudeRounded > 180) {
    return ['TMY longitudeRounded must be between -180 and 180.'];
  }
  if (tmy.datasetYear !== null && !Number.isInteger(tmy.datasetYear)) {
    return ['TMY datasetYear must be an integer when the provider declares one.'];
  }
  if (!isFiniteNumber(tmy.elevationM)) return ['TMY elevationM must be finite.'];
  if (!isFiniteNumber(tmy.timeZoneOffsetHours)
    || tmy.timeZoneOffsetHours < -14
    || tmy.timeZoneOffsetHours > 14) {
    return ['TMY timeZoneOffsetHours must be between -14 and 14.'];
  }
  if (typeof tmy.fetchedAt !== 'string' || tmy.fetchedAt.trim().length === 0) {
    return ['TMY fetchedAt provenance is required.'];
  }

  for (let index = 0; index < tmy.hours.length; index += 1) {
    const error = validateHour(tmy.hours[index], index);
    if (error) return [`TMY hour ${String(index)}: ${error}`];
  }
  return [];
}

function unavailable(
  reason: AdiabaticWaterScreeningUnavailableResult['unavailableReason'],
  validationErrors: readonly string[],
): AdiabaticWaterScreeningUnavailableResult {
  return {
    status: 'not-calculated',
    modelVersion: ADIABATIC_WATER_SCREENING_MODEL_VERSION,
    estimateBasis: 'tmy-hourly-psychrometric-screening',
    unavailableReason: reason,
    validationErrors,
    limitations: [
      'No annual or daily adiabatic-water quantity is returned unless all 8,760 hourly weather records and every engineering range pass validation.',
      'Monthly or annual climate summaries are not substituted because water duty depends on coincident hourly dry bulb, wet bulb, pressure, and threshold crossings.',
    ],
  };
}

function dryAirSpecificVolumeM3PerKg(
  dryBulbC: number,
  humidityRatio: number,
  pressureMPa: number,
): number {
  const pressureKPa = pressureMPa * KILOPASCALS_PER_MEGAPASCAL;
  return DRY_AIR_GAS_CONSTANT_KPA_M3_PER_KG_K
    * (dryBulbC + KELVIN_OFFSET)
    * (1 + (HUMIDITY_SPECIFIC_VOLUME_FACTOR * humidityRatio))
    / pressureKPa;
}

function endpointConfiguration(
  assumptions: ResolvedAssumptions,
  endpoint: EndpointConfiguration['endpoint'],
): EndpointConfiguration {
  if (endpoint === 'low') {
    return {
      endpoint,
      facilityHeatMw: assumptions.facilityHeatMw.low,
      utilization: assumptions.utilization.low,
      airflowM3sPerMw: assumptions.airflowM3sPerMw.low,
      saturationEffectiveness: assumptions.saturationEffectiveness.low,
      // A higher switch temperature produces fewer wet hours.
      activationThresholdC: assumptions.activationThresholdC.high,
      // Higher COC produces less makeup for the same evaporation.
      cyclesOfConcentration: assumptions.cyclesOfConcentration.high,
    };
  }
  return {
    endpoint,
    facilityHeatMw: assumptions.facilityHeatMw.high,
    utilization: assumptions.utilization.high,
    airflowM3sPerMw: assumptions.airflowM3sPerMw.high,
    saturationEffectiveness: assumptions.saturationEffectiveness.high,
    activationThresholdC: assumptions.activationThresholdC.low,
    cyclesOfConcentration: assumptions.cyclesOfConcentration.low,
  };
}

function calculateEndpoint(
  hours: readonly TmyHour[],
  configuration: EndpointConfiguration,
): AdiabaticWaterEndpointTrace {
  const operatingFacilityHeatMw = configuration.facilityHeatMw
    * configuration.utilization;
  const totalAirflowM3s = operatingFacilityHeatMw
    * configuration.airflowM3sPerMw;
  const makeupFactor = configuration.cyclesOfConcentration
    / (configuration.cyclesOfConcentration - 1);
  let activeHours = 0;
  let annualEvaporationGallons = 0;
  let peakActiveHourEvaporationGallonsPerHour = 0;
  let currentDayEvaporationGallons = 0;
  let currentDayActiveHours = 0;
  let peakDailyEvaporationGallons = -1;
  let peakDayIndex = 0;
  let peakDayActiveHours = 0;

  for (const [hourIndex, hour] of hours.entries()) {
    let evaporationGallonsPerHour = 0;
    if (hour.dryBulbC >= configuration.activationThresholdC) {
      activeHours += 1;
      currentDayActiveHours += 1;

      const inletHumidityRatio = humidityRatioFromRelativeHumidity(hour);
      const outletDryBulbC = hour.dryBulbC
        - configuration.saturationEffectiveness
        * (hour.dryBulbC - hour.wetBulbC);
      const outletHumidityRatio = humidityRatioFromThermodynamicWetBulb(
        outletDryBulbC,
        hour.wetBulbC,
        hour.pressureMPa,
      );
      const rawHumidityGain = outletHumidityRatio - inletHumidityRatio;
      if (rawHumidityGain < -NEGATIVE_HUMIDITY_GAIN_TOLERANCE) {
        throw new Error(
          `TMY hour ${String(hour.hour)} produced a negative humidity-ratio gain.`,
        );
      }
      const humidityGain = Math.max(0, rawHumidityGain);
      const inletSpecificVolumeM3PerKg = dryAirSpecificVolumeM3PerKg(
        hour.dryBulbC,
        inletHumidityRatio,
        hour.pressureMPa,
      );
      const dryAirMassFlowKgPerSecond = totalAirflowM3s
        / inletSpecificVolumeM3PerKg;
      const evaporationKgPerHour = dryAirMassFlowKgPerSecond
        * humidityGain
        * SECONDS_PER_HOUR;
      evaporationGallonsPerHour = evaporationKgPerHour
        / WATER_DENSITY_KG_PER_M3
        * US_GALLONS_PER_CUBIC_METER;
      if (!Number.isFinite(evaporationGallonsPerHour)
        || evaporationGallonsPerHour < 0) {
        throw new Error(
          `TMY hour ${String(hour.hour)} produced an invalid evaporation rate.`,
        );
      }
    }
    annualEvaporationGallons += evaporationGallonsPerHour;
    currentDayEvaporationGallons += evaporationGallonsPerHour;
    peakActiveHourEvaporationGallonsPerHour = Math.max(
      peakActiveHourEvaporationGallonsPerHour,
      evaporationGallonsPerHour,
    );

    if ((hourIndex + 1) % HOURS_PER_DAY === 0) {
      if (currentDayEvaporationGallons > peakDailyEvaporationGallons) {
        peakDailyEvaporationGallons = currentDayEvaporationGallons;
        peakDayIndex = Math.floor(hourIndex / HOURS_PER_DAY);
        peakDayActiveHours = currentDayActiveHours;
      }
      currentDayEvaporationGallons = 0;
      currentDayActiveHours = 0;
    }
  }

  const annualMakeupGallons = annualEvaporationGallons * makeupFactor;
  const peakDayStartHour = peakDayIndex * HOURS_PER_DAY;
  const peakDayFirstHour = hours[peakDayStartHour];
  return {
    endpoint: configuration.endpoint,
    facilityHeatMw: configuration.facilityHeatMw,
    utilization: configuration.utilization,
    operatingFacilityHeatMw,
    airflowM3sPerMw: configuration.airflowM3sPerMw,
    totalAirflowM3s,
    saturationEffectiveness: configuration.saturationEffectiveness,
    activationThresholdC: configuration.activationThresholdC,
    cyclesOfConcentration: configuration.cyclesOfConcentration,
    activeHours,
    annualEvaporationGallons,
    annualMakeupGallons,
    averageDailyEvaporationGallons: annualEvaporationGallons
      / DAYS_PER_NON_LEAP_YEAR,
    averageDailyMakeupGallons: annualMakeupGallons / DAYS_PER_NON_LEAP_YEAR,
    peakDailyEvaporationGallons,
    peakDailyMakeupGallons: peakDailyEvaporationGallons * makeupFactor,
    peakDay: {
      tmyDayIndex: peakDayIndex,
      startHour: peakDayStartHour,
      month: peakDayFirstHour.month,
      day: peakDayFirstHour.day,
      activeHours: peakDayActiveHours,
    },
    peakActiveHourEvaporationGallonsPerHour,
    peakActiveHourMakeupGallonsPerHour:
      peakActiveHourEvaporationGallonsPerHour * makeupFactor,
  };
}

function range(low: number, high: number): AdiabaticScreeningRange {
  return { low: Math.min(low, high), high: Math.max(low, high) };
}

/**
 * Calculate an hourly TMY screening envelope for recirculating adiabatic
 * pre-cooling. The function performs no I/O and returns identical output for
 * identical input.
 */
export function calculateAdiabaticWaterScreening(
  input: AdiabaticWaterScreeningInput,
): AdiabaticWaterScreeningResult {
  if (!input || typeof input !== 'object') {
    return unavailable('invalid-input', ['Adiabatic screening input is required.']);
  }
  const assumptions = resolveAssumptions(input);
  const inputErrors = validateInput(assumptions);
  if (inputErrors.length > 0) return unavailable('invalid-input', inputErrors);

  const tmyErrors = tmyDatasetValidationErrors(input.tmy);
  if (tmyErrors.length > 0) return unavailable('invalid-tmy', tmyErrors);

  let lowEndpoint: AdiabaticWaterEndpointTrace;
  let highEndpoint: AdiabaticWaterEndpointTrace;
  try {
    lowEndpoint = calculateEndpoint(
      input.tmy.hours,
      endpointConfiguration(assumptions, 'low'),
    );
    highEndpoint = calculateEndpoint(
      input.tmy.hours,
      endpointConfiguration(assumptions, 'high'),
    );
  } catch (error) {
    return unavailable('invalid-tmy', [
      error instanceof Error ? error.message : 'Hourly psychrometric calculation failed.',
    ]);
  }

  const operatingFacilityHeatMw = range(
    assumptions.facilityHeatMw.low * assumptions.utilization.low,
    assumptions.facilityHeatMw.high * assumptions.utilization.high,
  );

  return {
    status: 'calculated',
    modelVersion: ADIABATIC_WATER_SCREENING_MODEL_VERSION,
    estimateBasis: 'tmy-hourly-psychrometric-screening',
    unavailableReason: null,
    validationErrors: [],
    annualEvaporationGallons: range(
      lowEndpoint.annualEvaporationGallons,
      highEndpoint.annualEvaporationGallons,
    ),
    annualMakeupGallons: range(
      lowEndpoint.annualMakeupGallons,
      highEndpoint.annualMakeupGallons,
    ),
    averageDailyEvaporationGallons: range(
      lowEndpoint.averageDailyEvaporationGallons,
      highEndpoint.averageDailyEvaporationGallons,
    ),
    averageDailyMakeupGallons: range(
      lowEndpoint.averageDailyMakeupGallons,
      highEndpoint.averageDailyMakeupGallons,
    ),
    peakDailyEvaporationGallons: range(
      lowEndpoint.peakDailyEvaporationGallons,
      highEndpoint.peakDailyEvaporationGallons,
    ),
    peakDailyMakeupGallons: range(
      lowEndpoint.peakDailyMakeupGallons,
      highEndpoint.peakDailyMakeupGallons,
    ),
    activeHours: range(lowEndpoint.activeHours, highEndpoint.activeHours),
    peakActiveHourEvaporationGallonsPerHour: range(
      lowEndpoint.peakActiveHourEvaporationGallonsPerHour,
      highEndpoint.peakActiveHourEvaporationGallonsPerHour,
    ),
    peakActiveHourMakeupGallonsPerHour: range(
      lowEndpoint.peakActiveHourMakeupGallonsPerHour,
      highEndpoint.peakActiveHourMakeupGallonsPerHour,
    ),
    assumptions: {
      facilityHeatMw: assumptions.facilityHeatMw,
      utilization: assumptions.utilization,
      operatingFacilityHeatMw,
      airflowM3sPerMw: assumptions.airflowM3sPerMw,
      saturationEffectiveness: assumptions.saturationEffectiveness,
      activationThresholdC: assumptions.activationThresholdC,
      cyclesOfConcentration: assumptions.cyclesOfConcentration,
      waterDensityKgPerM3: WATER_DENSITY_KG_PER_M3,
    },
    trace: {
      validTmyHours: REQUIRED_TMY_HOURS,
      hourDurationSeconds: SECONDS_PER_HOUR,
      activationRule: 'dryBulbC >= activationThresholdC',
      endpointMethod: 'The low endpoint pairs minimum heat, utilization, airflow, and effectiveness with the higher activation threshold and higher COC; the high endpoint uses the converse assumptions.',
      equations: [
        'T_out_C = T_db_C - effectiveness * (T_db_C - T_wb_C)',
        'm_dry_air_kg_s = airflow_m3_s / inlet_specific_volume_m3_kg_dry_air',
        'evaporation_kg_h = m_dry_air_kg_s * (W_out - W_in) * 3600',
        'makeup = evaporation * COC / (COC - 1)',
        'daily_evaporation_gal = sum(hourly evaporation gallons in each contiguous 24-hour TMY day)',
        'peak_daily_makeup_gal = max(daily evaporation gallons) * COC / (COC - 1)',
      ],
      endpoints: [lowEndpoint, highEndpoint],
    },
    provenance: {
      tmy: {
        schemaVersion: input.tmy.schemaVersion,
        source: input.tmy.source,
        ...(input.tmy.sourceVersion ? { sourceVersion: input.tmy.sourceVersion } : {}),
        ...(input.tmy.datasetName ? { datasetName: input.tmy.datasetName } : {}),
        datasetYear: input.tmy.datasetYear,
        latitudeRounded: input.tmy.latitudeRounded,
        longitudeRounded: input.tmy.longitudeRounded,
        ...(input.tmy.gridLatitude != null ? { gridLatitude: input.tmy.gridLatitude } : {}),
        ...(input.tmy.gridLongitude != null ? { gridLongitude: input.tmy.gridLongitude } : {}),
        ...(input.tmy.locationId ? { locationId: input.tmy.locationId } : {}),
        elevationM: input.tmy.elevationM,
        timeZoneOffsetHours: input.tmy.timeZoneOffsetHours,
        fetchedAt: input.tmy.fetchedAt,
        hourCount: REQUIRED_TMY_HOURS,
      },
      sources: ADIABATIC_WATER_SCREENING_SOURCE_REFS,
    },
    limitations: [
      'This is a screening estimate of adiabatic inlet-air pre-cooling water, not metered use, permitted demand, or an OEM selection.',
      'Facility heat and utilization scale the generic installed airflow proxy; heat load does not independently determine adiabatic evaporation.',
      'The threshold cases assume full airflow and constant utilization whenever the hourly dry bulb reaches the switch point; staging, fan curves, partial wetting, hysteresis, and control deadbands require OEM data.',
      'The 26.67 C and 29.44 C activation points are screening control cases, not universal equipment setpoints.',
      'Recirculating makeup assumes dissolved-solids steady state at the stated COC. Initial pad wetting, start/stop flushes, leaks, basin dumps, and water-quality-specific treatment are excluded.',
      'Dry-mode water remains conditionally zero only when the selected equipment is verified to reject full design heat at the local design dry bulb and required supply temperature.',
      'A full-duty design-capacity check still requires process-fluid temperatures and flow, coil and fan curves, redundancy, fouling margin, and the OEM control sequence.',
    ],
  };
}
